import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, FormControl, FormLabel, HStack, IconButton, Input, List, ListItem, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, ProgressLabel, Select, Spacer, Text, VStack } from "@chakra-ui/react";
import { Result } from "../../../Api/api2";
import { Competition_GET } from "../../../Api/dto/Competition_GET";
import { Discipline_GET } from "../../../Api/dto/Discipline_GET";

import { useState } from "react";
import BirthDatePicker from "../../DatePicker/DatePicker";
import { useAwaitResponse } from "../../../Api/HttpRequestHelpers";
import { DisciplineClient} from "../../../Api/client/CompetitionClient";
import InfoPopover from "../../Helpers/InfoPopover";
import { Gender } from "../../../Api/dto/Discipline_POST_PUT";
import { DownloadIcon, TrashIcon, UndoIcon } from "lucide-react";
import NewAgeGroupModal from "./NewAgeGroupModal";
import { resultApi } from "../../../Api/BackendClient";
import { ResultExtendedDto_GET } from "../../../Api/dto/ResultExtendedDto_GET";
import { CSVLink } from "react-csv";
import { date } from "zod";


export interface AgeGroup {
  distanceInMeters: number;
  disciplineId: number;
  gender: Gender;
  maxAge: number;
  name: string;
}


interface ResultReportingCsvData {
  type: string; // "väg", "terräng", "trail", "OCR"
  race_name: string;
  city: string;
  date: string;
  organizer: string;
  distance: string;
  gender: string;
  course_measurer: string; // "" for odd distances
  date_of_measurement: string; // "" for odd distances
  placing: string; // Placing within own gender
  agegroup: string; // "" if men/women, "P13",... in the other cases
  agegroupplacing: string; // Placing within own agegroup or "" if no agagroup
  extra: string; // "" for stadsloppet
  firstname: string; // Runner firstname
  lastname: string; // Runner lastname
  country: string; // "" for all swedish runners
  club: string; // "" for no club
  birthdate: string; // "" we use yb instead
  yb: string; // Year of birth
  result: string; // "hh:mm:ss" or "mm:ss", DNF, DNS, DQ
  netto: string; // ""
  splits: string; // ""
}

const headers = [
  "type",
  "race_name",
  "city",
  "date",
  "organizer",
  "distance",
  "gender",
  "course_measurer",
  "date_of_measurement",
  "placing",
  "agegroup",
  "agegroupplacing",
  "extra",
  "firstname",
  "lastname",
  "country",
  "club",
  "birthdate",
  "yb",
  "result",
  "netto",
  "splits",
];

interface Distance {
  distanceInMeters: number;
  type: "Väg" | "Terräng" | "Trail";
  measuredBy: string | null;
  measuredAt: Date | null;
}

function mayBeMeasured(d: Distance) {
  return d.type === "Väg" && [5000, 10000, 21100, 42200].includes(d.distanceInMeters);
}

export function ResultDownloadPopupView(props: {
  //results: Result[];
  competition: Competition_GET;
  //disciplines: Discipline_GET[];
}) {

  const [disciplines, isLoadingDisciplines] = useAwaitResponse(
    [] as Discipline_GET[],
    () => DisciplineClient.getDisciplines(props.competition.tag!),
    (response) => response.body?.sort((a,b) => a.displayOrder - b.displayOrder) ?? ([] as Discipline_GET[])
  );

  const [show, setShow] = useState(false);

  if (isLoadingDisciplines) {
    return <>Loading...</>;
  }

  return (show ? <Modal isOpen={true} 
    closeOnOverlayClick={false}
    scrollBehavior="inside"
    onEsc={() => {}}
    closeOnEsc={false}
    size={"full"}
    onClose={() => {setShow(false);}}>
      <ModalOverlay />
      <ModalContent
      background="white" 
      style={{display: "flex", flexDirection: "column", height: "100%"}}>
        <ModalHeader fontSize={{ base: "sm", sm: "md" }}>
          Betala/Slutför anmälan
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          scrollBehavior={"auto"}
          scrollSnapType={"block"}
          marginTop={"0px"}
          margin={"20px"}
          overflowY={"auto"}
        >
          <ResultDownloadView competition={props.competition} disciplines={disciplines} />
          </ModalBody>
      </ModalContent>
  </Modal>
  : <Button 
              margin={"0px"}
                padding={"30px"}
                width={"80%"}
                background="blue.400"
                colorScheme="green"
                rightIcon={<DownloadIcon/>}
  onClick={() => setShow(true)}>Resultat (rapport)</Button>
  );
}

export function ResultDownloadView(props: {
  competition: Competition_GET;
  disciplines: Discipline_GET[];
 }) {
  const [race_name, set_race_name] = useState<string>(props.competition.name?.replaceAll(/\d|\s/g,"") ?? "");
  const [city, set_city] = useState<string>("Östhammar");
  const [date, set_date] = useState<Date | null>(props.competition.startTime ? new Date(props.competition.startTime) : null);
  const [organizer, set_organizer] = useState<string>("Östhammar FI");
  const [presentableResults, setPresentableResults] = useState<ResultReportingCsvData[]>([]);
  const [generatedDate, setGeneratedDate] = useState<Date | null>(null);

  const [distances, set_distances] = useState<Distance[]>(
      props.disciplines
        .map((d) => d.distanceInMeters)
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort((a, b) => a - b)
        .map((d) => ({
          distanceInMeters: d,
          type: "Väg",
          measuredBy: null,
          measuredAt: null
        })) 
  );

  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>(
    props.disciplines.flatMap((d) => 
      disciplineToAgeGroups(d)
    )
  );

  const [droppedDisciplineIds, setDroppedDisciplineIds] = useState<number[]>([]);


  return (
<VStack spacing={6} align="stretch" maxW="600px" mx="auto" mt={10}>
  <Text fontSize="2xl" fontWeight="bold">
    Ladda ner inrapporteringslista
  </Text>

  <BeforeYouStartBox/>

  <Box p={6} borderWidth="1px" borderRadius="lg" boxShadow="md">
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <VStack spacing={4} align="stretch">

        <FormControl>
          <FormLabel>Loppets namn</FormLabel>
          <Input value={race_name ?? ""} onChange={(e) => set_race_name(e.target.value)} />
        </FormControl>

        <FormControl>
          <FormLabel>Stad</FormLabel>
          <Input value={city ?? ""} onChange={(e) => set_city(e.target.value)} />
        </FormControl>

        <FormControl>
          <FormLabel>Datum</FormLabel>
          <BirthDatePicker
            allowSettingAnyBirthYear
            maxYear={new Date().getFullYear()}
            minYear={2024}
            value={date}
            onChange={(d) => set_date(d)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Arrangör</FormLabel>
          <Input value={organizer ?? ""} onChange={(e) => set_organizer(e.target.value)} />
        </FormControl>

        {/* Distances section */}
        <Box pt={4}>
          <Text fontWeight="semibold" mb={2}>
            Banor
          </Text>

          <VStack spacing={4} align="stretch">
            {distances.map((d) => (
              <Box
                key={d.distanceInMeters}
                p={4}
                borderWidth="1px"
                borderRadius="md"
                bg="gray.50"
              >
                <Text fontWeight="bold" mb={2}>
                  {d.distanceInMeters / 1000} km
                </Text>

                <VStack spacing={3} align="stretch">
                  <HStack>
                    <Text minW="80px">Typ</Text>
                    <Select
                      background={"white"}
                      value={d.type}
                      onChange={(e) =>
                        set_distances((old) =>
                          old.map((dist) =>
                            dist.distanceInMeters === d.distanceInMeters
                              ? { ...dist, type: e.target.value as "Väg" | "Terräng" | "Trail" }
                              : dist
                          )
                        )
                      }
                    >
                      <option value="Väg">Väg</option>
                      <option value="Terräng">Terräng</option>
                      <option value="Trail">Trail</option>
                    </Select>
                  </HStack>

                  <HStack>
                    <Text minW="80px">Banmätare</Text>
                    <InfoPopover content="Förnamn och efternamn på förbundsmätaren som utfört mätningen" />
                    <Input
                      background={mayBeMeasured(d) ? "white" : undefined}
                      disabled={!mayBeMeasured(d)}
                      value={d.measuredBy ?? ""}
                      placeholder="Ej kontrollmätt"
                      onChange={(e) =>
                        set_distances((old) =>
                          old.map((dist) =>
                            dist.distanceInMeters === d.distanceInMeters
                              ? { ...dist, measuredBy: e.target.value }
                              : dist
                          )
                        )
                      }
                    />
                  </HStack>

                  <HStack>
                    <Text minW="80px">Mätdatum</Text>
                    <InfoPopover content="Datum då mätningen utfördes" />
                    <BirthDatePicker
                      disabled={!mayBeMeasured(d)}
                      value={d.measuredAt}
                      minYear={2000}
                      maxYear={new Date().getFullYear()}
                      onChange={(date) =>
                        set_distances((old) =>
                          old.map((dist) =>
                            dist.distanceInMeters === d.distanceInMeters
                              ? { ...dist, measuredAt: date }
                              : dist
                          )
                        )
                      }
                    />
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Discipline section */}
        <Box pt={2}>
              <Text fontWeight="semibold" mb={2} >
                Vilka åldersgrupper ska klasserna delas upp i?
              </Text>
          <VStack spacing={4} align="stretch">
            {props.disciplines.map((d, index) => {
              const deleted = droppedDisciplineIds.includes(d.id!);
              return (
              <Box key={index} p={4} borderWidth="1px" borderRadius="md" bg="gray.50" background={deleted ? "red.300" : "gray.50"}>
                <HStack>
                <Text fontWeight="bold" mb={2}>
                  {`Klass: ${d.name}`}
                </Text>
                <Spacer/>
                <IconButton aria-label="Ta bort klass" icon={deleted ? <UndoIcon/> : <TrashIcon />} background={"white"} colorScheme={deleted ? "green" : "red"} variant="outline" onClick={() => {
                  setDroppedDisciplineIds((old) => {
                    if (old.includes(d.id!)) {
                      return old.filter((id) => id !== d.id!);
                    }
                    return [...old, d.id!];
                  });
                }}/>
                </HStack>
                {deleted || <HStack>
                  <Text>Åldersgrupper:</Text>
                {ageGroups
                .filter((ag) => ag.disciplineId === d.id)
                .map((ag, agIndex) => (
                  <Button key={agIndex} colorScheme="blue" variant="outline" onClick={() => {
                    setAgeGroups((old) => old.filter((a) => !(a.name === ag.name && a.disciplineId === ag.disciplineId)));
                  }}>
                    {`x ${ag.name}`}
                  </Button>
                ))}
                <NewAgeGroupModal onCreate={(maxAge, gender) => {
                  const newAgeGroup = {
                    name: getAgeGroupName(maxAge, gender),
                    distanceInMeters: d.distanceInMeters,
                    disciplineId: d.id!,
                    maxAge,
                    gender
                  };
                  setAgeGroups((old) => [...old, newAgeGroup]);
                }} />
                </HStack>
                }
              </Box>
            );
            })}
          </VStack>
        </Box>

<HStack>

        <Button colorScheme="blue" mt={4} 

        onClick={(e) => {
          e.preventDefault();
            fetchReport(
              {
              competition: props.competition,
              disciplines: props.disciplines.filter(d => !droppedDisciplineIds.includes(d.id!)), 
              ageGroups, 
              distances,
              race_name,
              city,
              date: formatDateYYYYMMDD(date),
              organizer
              }
            ).then((newPresentableResults) => {
              setPresentableResults(newPresentableResults);
              setGeneratedDate(new Date());
            });
        }}>
          Generera rapport
        </Button>

        <ResultDownloadLink results={presentableResults} competition={props.competition} generatedDate={generatedDate} />
</HStack>

      </VStack>
    </form>
  </Box>
</VStack>
  );
}


function getAgeGroupName(maxAge: number, gender: Gender): string {
  if (maxAge < 22) {
    return gender === Gender.MAN ? `P${maxAge}` : `F${maxAge}`;
  } else if (maxAge <= 100) {
    return gender === Gender.MAN ? `M${maxAge}` : `K${maxAge}`;
  }
  return "";
}

function disciplineToAgeGroups(discipline: Discipline_GET) {
  let ageGroups = [] as AgeGroup[]; 
  if (discipline.maxAge) {
    if (discipline.maxAge < 22) {
      ageGroups = discipline.genderRequirements.map((g) => {
          const name = g === Gender.MAN ? `P${discipline.maxAge}` : `F${discipline.maxAge}`;
        return {
          name,
          disciplineId: discipline.id!,
          gender: g
      } as AgeGroup});
      if (discipline.minAge && discipline.maxAge - discipline.minAge > 2) { 
        ageGroups.push(...discipline.genderRequirements.map((g) => {
          const name = g === Gender.MAN ? `P${discipline.maxAge! - 2}` : `F${discipline.maxAge! - 2}`;
          return {
            name,
            disciplineId: discipline.id!,
            gender: g,
          } as AgeGroup;
        }));
      }
    }
    else if (discipline.maxAge <= 100) {
      ageGroups = discipline.genderRequirements.map((g) => {
          const name = g === Gender.MAN ? `M${discipline.maxAge}` : `K${discipline.maxAge}`;
        return {
          name,
          disciplineId: discipline.id!,
          maxAge: discipline.maxAge,
          gender: g
      } as AgeGroup});
    }


  }
  return ageGroups;
}


async function fetchReport({
  competition,
  disciplines,
  ageGroups,
  distances,
  race_name,
  city,
  date,
  organizer
}: {
  competition: Competition_GET;
  disciplines: Discipline_GET[];
  ageGroups: AgeGroup[];
  distances: Distance[];
  race_name: string;
  city: string;
  date: string;
  organizer: string;
}) {
  return resultApi.getExtendedResults(disciplines.map(d => d.id!), competition.tag!).then((results) => {
    // Process the results and trigger the download
    distances.sort((a, b) => a.distanceInMeters - b.distanceInMeters);
    results.sort((a, b) => {
      if (a.dnsOrDnf == null && b.dnsOrDnf == null) {
       return a.timeInMs! - b.timeInMs!;
      }
      else if (a.dnsOrDnf == null) {
        return -1;
      }
      else if (b.dnsOrDnf == null) {
        return 1;
      }
      else if (a.dnsOrDnf === b.dnsOrDnf) {
        return 0;
      }
      else if (a.dnsOrDnf === "DNF") {
        return -1;
      }
      else {
        return 1;
      }
    }
  );

    const presentableResults : ResultReportingCsvData[] = [];
    for (let dist of distances) {
      const resultsForThisDistance = results.filter(r => r.distanceInMeters === dist.distanceInMeters);

      const womanResults = resultsForThisDistance.filter(r => r.gender === Gender.WOMAN);
      const completedWomanResults = completeResults(race_name, city, date, organizer, "K", womanResults, ageGroups, dist, competition);
      presentableResults.push(...completedWomanResults);

      const manResults = resultsForThisDistance.filter(r => r.gender === Gender.MAN);
      const completedManResults = completeResults(race_name, city, date, organizer, "M", manResults, ageGroups, dist, competition);
      presentableResults.push(...completedManResults);
    }
    return presentableResults;
  });
}


function formatYYMMDD(date: Date | null): string {
  if (!date) {
    return "";
  }
  const yy = date.getFullYear().toString().slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yy}${mm}${dd}`;
}

function formatDateYYYYMMDD(date: Date | null): string {
  if (!date) {
    return "";
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

// yyyy-MM-dd
function formatBirthDate(ssn: string | null, fullBirthYear: number): string {
  const birthdateOrYear = ssn?.replaceAll(/-[0-9Xx]*/g, "") || fullBirthYear.toString();
  if (birthdateOrYear.length === 4) {
    //return `${birthdateOrYear}-01-01`;  NOTE: This is not allowed
    return "";
  }
  else if (birthdateOrYear.length === 8) {
    return `${birthdateOrYear.slice(0, 4)}-${birthdateOrYear.slice(4, 6)}-${birthdateOrYear.slice(6, 8)}`;
  }
  return "";
}


//h:mm:ss OR mm:ss
function formatResult(timeInMs: number): string {
  const totalSeconds = Math.floor(timeInMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${String(minutes)}:${String(seconds).padStart(2, "0")}`;
}

function findAgeGroup(ageGroups: AgeGroup[], result: ResultExtendedDto_GET, competition: Competition_GET) {
  const resultAge = new Date(competition.startTime || Date.now()).getFullYear() - result.fullBirthYear;

  return ageGroups.filter(
    ag => ag.disciplineId === result.disciplineId && 
    ag.gender === result.gender && 
    resultAge <= ag.maxAge
  ).sort((a, b) => a.maxAge - b.maxAge)
  .find(() => true);
}



function completeResults(race_name: string, city: string, date: string, organizer: string, gender: string, sortedResultForGenderAndDistance: ResultExtendedDto_GET[], ageGroups: AgeGroup[], distance: Distance, competition: Competition_GET) {
  let presentableResults : ResultReportingCsvData[] = [];
  let nextAgeGroupPosition = new Map<string, number>();
  sortedResultForGenderAndDistance.forEach((r, i) => {
    const ageGroup = findAgeGroup(ageGroups, r, competition)?.name ?? "";

    let ageGroupPosition = null;
    if (ageGroup != null) {
      ageGroupPosition = nextAgeGroupPosition.get(ageGroup) ?? 1;
      nextAgeGroupPosition.set(ageGroup, ageGroupPosition  + 1);
    } 

    presentableResults.push({
      type: distance.type,
      race_name: race_name,
      city: city,
      date: date,
      organizer: organizer,
      distance: `${distance.distanceInMeters / 1000} km`,
      gender: gender,//"K",
      course_measurer: distance.measuredBy ?? (mayBeMeasured(distance) ? "Ej kontrollmätt" : ""),
      date_of_measurement: formatYYMMDD(distance.measuredAt),
      placing: `${r.dnsOrDnf ? "" : i + 1}`,
      agegroup: ageGroup,
      agegroupplacing: `${r.dnsOrDnf ? "" : (ageGroupPosition ?? "")}`,
      extra: "",
      firstname: r.firstname,
      lastname: r.lastname,
      country: "",
      club: r.sportsTeam,
      birthdate: formatBirthDate(r.ssn, r.fullBirthYear),
      yb: `${r.fullBirthYear}`,
      result: r.dnsOrDnf ?? formatResult(r.timeInMs!),
      netto: "",
      splits: ""
    } as ResultReportingCsvData);
  });
  return presentableResults;
}

export function ResultDownloadLink(props: {
  results : ResultReportingCsvData[],
  competition: Competition_GET;
  generatedDate: Date | null;
}) {
  // DateTimeStr on format yyyMMMdd_HHmm 
  const dateTimeStr = formatYYYYMMDD_HHmm(props.generatedDate);
  const fileName = `resultat-${props.competition.tag}_${dateTimeStr}.csv`;
  return (
    props.generatedDate != null ?
    <Button
      as={CSVLink}
      colorScheme="green"
      mt={4}
      filename={fileName}
      data={props.results}
      headers={headers.map((h) => ({
        label: h,
        key: h,
      }))}
      height={"fit-content"}
    >
       <VStack textAlign="center" padding={"10px"}>
        <Text>Ladda ner</Text>
        <Text fontSize="sm" opacity={0.8}>
          {fileName}
        </Text>
      </VStack>
    </Button> 
    : <></>
  );
}

function formatYYYYMMDD_HHmm(date: Date | null): string {
  if (date == null) {
    return "";
  }
  const pad = (n: number) => n.toString().padStart(2, "0");

  const yy = date.getFullYear().toString().slice(-2);
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());

  return `${yy}${mm}${dd}-${hh}${min}`;
}




export function BeforeYouStartBox() {
  return (
    <Alert status="info" borderRadius="md" alignItems="flex-start">
      <AlertIcon />

      <Box>
        <AlertTitle mb={2}>Att tänka på:</AlertTitle>

        <AlertDescription>
          <List spacing={2} styleType="decimal" pl={4}>
            <ListItem>
              Ta bort alla klasser vars resultat inte ska redovisas (vanligen knatteloppet, hundklass och hinderbana)
            </ListItem>

            <ListItem>
              Lägg till eller ta bort eventuellt felaktiga åldersgrupper som springer i de olika klasserna.
            </ListItem>

            <ListItem>
              Döp filen med 6-siffrigt datum, ort och tävlingsnamn med mellanslag mellan.
              Exempel: 240911 Östhammar Stadsloppet.csv
            </ListItem>

            <ListItem>
              Gå igenom den genererade rapporten och verifiera att innehållet stämmer mot svenska friidrottsförbundets fil
              <Text as="span" fontStyle="italic">
                {" "}”Specifikation resultatlistor för långlopp”
              </Text>
            </ListItem>

            <ListItem>
              Skicka in rapporten enligt instruktionerna i specifikations filen
            </ListItem>
          </List>

          <Box mt={4} p={3} borderWidth="1px" borderRadius="md" bg="yellow.50">
            <Text fontWeight="bold">TODO – till nästa år</Text>
            <Text>
              Tvinga folk att ange klubb ELLER hemort i anmälan. Det behövs egentligen för rapporteringen.
            </Text>
          </Box>
        </AlertDescription>
      </Box>
    </Alert>
  );
}